/**
 * Blog post METADATA only — no `body` field, no React imports.
 *
 * Why this file exists (Lighthouse iter 142, May 2026):
 *   The landing page's <LatestFromBlogSection> needs only the 10 post
 *   metas to render its "From the Blog" cards. When it imported from
 *   `./blogPosts.tsx`, Webpack pulled the entire 1,558-line file into
 *   the main bundle — including ~31KB of body JSX that's only ever
 *   rendered on /blog/* routes. Lighthouse flagged the 29KB as
 *   wasted-JS on the landing page.
 *
 *   By splitting metas into this small dedicated module, the landing
 *   page gets cards with zero body-component baggage, and the heavy
 *   `blogPosts.tsx` is loaded only when the (lazy) blog routes mount.
 *
 *   Keep this list in sync with `BLOG_POSTS` in blogPosts.tsx — that
 *   file now imports `BLOG_POST_METAS` from here and zips each entry
 *   with its body component.
 */

export interface BlogPostMeta {
  slug: string;
  title: string;
  description: string;
  /** ISO date string, e.g. "2026-02-14" */
  publishedAt: string;
  /** Short read-time label shown on index. */
  readTime: string;
  /** One-line hook for index cards + meta description fallback. */
  excerpt: string;
  /** Keywords comma-separated for meta tag. */
  keywords: string;
  /** Accent color hex for hero + badges. */
  accent: string;
  /** Category tag shown on cards. */
  category: string;
}

export const BLOG_POST_METAS: BlogPostMeta[] = [
  {
    slug: 'why-most-men-over-40-never-get-lean',
    title: 'Why most men over 40 never get lean — in 16 slides',
    description: 'Sixteen slides on the stealth surplus, the weekend reset, ego lifting and the one skill nobody practises. A retelling of Kevin Boyle\'s 11-years-a-trainer talk.',
    excerpt: 'The hidden calories you never sat down for, why the weekend cancels the week, and the skill of being slightly hungry without treating it as an emergency.',
    publishedAt: '2026-10-18',
    readTime: '6 min read',
    keywords: 'fat loss over 40, why men over 40 cannot lose fat, hidden calories, weekend weight gain, ego lifting, fat loss vs weight loss, discipline vs motivation',
    accent: '#cafd00',
    category: 'Coach Voices',
  },
  {
    slug: 'how-ai-estimates-body-fat-from-a-photo',
    title: 'How AI Estimates Body Fat Percentage from a Photo (And How Accurate It Really Is)',
    description: 'A plain-English look at how AI analyzes progress photos to estimate body fat percentage, what it gets right, where it fails, and the 5 rules for an accurate reading.',
    excerpt: 'How AI reads your silhouette, shadows, and muscle definition to estimate body fat — and the two physics problems that fight the model.',
    publishedAt: '2026-02-10',
    readTime: '6 min read',
    keywords: 'AI body fat from photo, body fat calculator without calipers, how AI estimates body fat, body fat percentage photo, AI body composition, how to measure body fat at home',
    accent: '#00f1fe',
    category: 'AI & Body Composition',
  },
  {
    slug: 'how-to-take-progress-photos-for-ai-body-fat',
    title: 'How to Take Progress Pictures for Accurate AI Body Fat Analysis',
    description: 'The 7-point checklist for progress photos that give AI body-fat tools a reliable read. Distance, lighting, pose, timing — plus the mistakes that destroy accuracy.',
    excerpt: 'Why your photo conditions move AI body-fat readings by 4-6%, and the exact 7-point setup that makes weekly trend tracking trustworthy.',
    publishedAt: '2026-02-15',
    readTime: '6 min read',
    keywords: 'how to take progress photos, progress photo tips, AI body fat photo accuracy, fitness progress photo guide, weekly progress photos',
    accent: '#00f1fe',
    category: 'AI & Body Composition',
  },
  {
    slug: 'body-fat-percentage-chart-men-women',
    title: 'Body Fat Percentage Chart for Men and Women (With Visual Descriptions)',
    description: 'Complete body fat percentage chart for men and women with visual descriptions of each range — athletic, fitness, average — plus realistic goal-setting guidance.',
    excerpt: 'What 10%, 15%, 20%, 25% body fat actually looks like — with realistic target ranges for men and women based on ACE and ACSM data.',
    publishedAt: '2026-02-12',
    readTime: '5 min read',
    keywords: 'body fat percentage chart, what body fat percentage am I, athletic body fat percentage, body fat range men, body fat range women, body fat visual chart',
    accent: '#cafd00',
    category: 'Reference',
  },
  {
    slug: 'what-is-a-good-body-fat-percentage-by-age',
    title: 'What is a good body fat percentage for each age group? (Charts for Men and Women)',
    description: 'Age-adjusted body fat percentage charts for men and women from 20 to 60+, with the biology of why targets rise with age and how to pick a realistic goal.',
    excerpt: 'A 25-year-old at 18% body fat reads differently than a 55-year-old at 18%. The age-adjusted charts, plus when "lean" becomes unhealthy.',
    publishedAt: '2026-02-16',
    readTime: '6 min read',
    keywords: 'body fat percentage by age, healthy body fat percentage by age, body fat chart by age, body fat for my age, body fat men by age, body fat women by age',
    accent: '#cafd00',
    category: 'Reference',
  },
  {
    slug: 'calorie-deficit-calculator-guide',
    title: 'Calorie Deficit Calculator Guide: How to Lose Weight Without Losing Muscle',
    description: 'How to calculate your calorie deficit for weight loss — TDEE, deficit size, protein target, and the 4 mistakes that stall 80% of cuts.',
    excerpt: 'TDEE → deficit → protein target → 7-day moving average. The full formula for losing 1 pound per week sustainably, without the rebound.',
    publishedAt: '2026-02-14',
    readTime: '7 min read',
    keywords: 'calorie deficit calculator, how many calories should I eat to lose weight, 1 pound per week deficit, calorie deficit for weight loss, TDEE calculator, how much protein to eat on a cut',
    accent: '#d277ff',
    category: 'Nutrition',
  },
  {
    slug: 'how-much-protein-per-day-to-build-muscle',
    title: 'How Much Protein You Should Eat Every Day to Build Muscle (Real Numbers, Not Bro-Science)',
    description: 'Research-backed protein targets to build muscle — 1.6 to 2.2 g/kg of bodyweight per day, with a full day of example meals hitting 150g.',
    excerpt: '1.6-2.2g per kg of bodyweight — backed by a 2018 meta-analysis of 49 studies. Why higher is just expensive pee, and how to hit the target with real food.',
    publishedAt: '2026-02-17',
    readTime: '6 min read',
    keywords: 'how much protein per day, protein per day to build muscle, protein per kg bodyweight, how much protein per day to gain muscle, protein intake for muscle growth, daily protein requirement',
    accent: '#d277ff',
    category: 'Nutrition',
  },
  {
    slug: 'how-to-read-a-restaurant-menu-for-calories',
    title: 'The 60-Second Scan: How to Read a Restaurant Menu for Calories',
    description: 'The 5 hidden calorie sources restaurants never tell you about, the menu keywords that signal calorie density, and the 5 safer orders for any cuisine.',
    excerpt: 'Why "grilled chicken salad" can be 1,100 calories, and how 6 menu keywords tell you everything about calorie density in under a minute.',
    publishedAt: '2026-02-18',
    readTime: '6 min read',
    keywords: 'restaurant calorie guide, hidden calories restaurant, how to eat out on a diet, low calorie restaurant orders, restaurant menu calories, eating out while dieting',
    accent: '#ff7351',
    category: 'Nutrition',
  },
  {
    slug: 'best-exercises-for-biceps-peak',
    title: 'Best Exercises for Biceps Peak (And the Honest Truth About Genetics)',
    description: 'The 4 exercises that actually bias the long head of the biceps for a taller peak, the 3 myths to ignore, and a 4-week peak-focused routine.',
    excerpt: 'Why biceps peak is 70% genetics and 30% training — and the exact 30% you can change with long-head-biased curls.',
    publishedAt: '2026-02-19',
    readTime: '7 min read',
    keywords: 'biceps peak exercises, how to get taller biceps peak, long head biceps exercises, biceps peak workout, exercises for biceps peak, how to build biceps peak',
    accent: '#cafd00',
    category: 'Muscle Building',
  },
  {
    slug: 'apre-10-autoregulation-explained',
    title: 'APRE 10: The Quiet Algorithm That Picks Your Working Weight For You',
    description: "How the APRE 10 (Autoregulatory Progressive Resistance Exercise) protocol works — Mann's 4-set scheme, the adjustment table, when to use it, and the two mistakes that ruin it.",
    excerpt: "Mann's D-1 football protocol in 4 sets. Why the algorithm out-progresses linear programs by ~2x on bench and squat — and how to run it without thinking.",
    publishedAt: '2026-05-26',
    readTime: '7 min read',
    keywords: 'APRE 10 protocol, autoregulation training, Bryan Mann APRE, autoregulatory progressive resistance, APRE program, how to run APRE 10, autoregulated strength training',
    accent: '#cafd00',
    category: 'Programming',
  },
  {
    slug: 'dup-3-daily-undulating-periodization',
    title: 'DUP-3: Why I Train the Same Muscle at Three Different Rep Ranges Each Week',
    description: 'Daily Undulating Periodization explained: how rotating 12-rep, 6-rep, and 25-rep days through the same week outperforms linear periodization for hypertrophy and strength.',
    excerpt: 'Rhea 2002: same exercises, same total volume, half the time — DUP gained 2x more strength than linear periodization. The 3-rotation version, in plain words.',
    publishedAt: '2026-05-27',
    readTime: '8 min read',
    keywords: 'daily undulating periodization, DUP training, DUP 3 day, Rhea 2002 DUP study, undulating periodization vs linear, hypertrophy strength endurance same week, myo-reps DUP',
    accent: '#00f1fe',
    category: 'Programming',
  },
];
